import { useBooleanState } from '@retailer-platform/shared-components'
import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { FormattedDate } from 'react-intl'
import { useDomainMessages } from 'domains/collections/utils/domain/intl'
import { useGetCollectionQuery, useGetParentCollectionLazyQuery } from 'domains/collections/api'
import { CollectionsViewInfo } from 'domains/collections/components/collections-view/components/CollectionsViewInfo'
import { CollectionsViewRow } from 'domains/collections/components/collections-view/components/CollectionsViewRow'
import { CollectionsViewChildCollections } from 'domains/collections/components/collections-view/components/CollectionsViewChildCollections'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { CollectionsViewSection } from 'domains/collections/components/collections-view/components/CollectionsViewSection'
import { CollectionsColorInfo } from 'domains/collections/components/collections-view/components/CollectionsViewColor'
import { CollectionsViewImage } from 'domains/collections/components/collections-view/components/CollectionsViewImage'
import { CollectionsViewHeader } from 'domains/collections/components/collections-view/components/CollectionsViewHeader'
import { CollectionsDeleteCollectionModal } from 'domains/collections/components/collections-delete/delete-collection-modal/CollectionsDeleteCollectionModal'
import { type CollectionsRoutingConfig } from 'domains/collections/types/collections.types'
import { CollectionsViewRule } from 'domains/collections/components/collections-view/components/CollectionsViewRule'
import {
  canCurrentRetailerModifyCollection,
  collectionStatusMessage,
} from 'domains/collections/utils/collections.utils'
import { useDomainGoToPath } from 'domains/collections/utils/domain/routing'

export interface CollectionsViewProps {
  collectionId: string
  retailerId: string
  routingConfig: CollectionsRoutingConfig
  baseBreadcrumbs?: React.ReactChild
}

const PageWrapper = styled.div({
  width: '100%',
  backgroundColor: 'white',
})

const PageContent = styled.div({
  width: 900,
  margin: '0 auto',
})

export const CollectionsView: React.FunctionComponent<CollectionsViewProps> = ({
  collectionId,
  retailerId,
  routingConfig,
  baseBreadcrumbs,
}) => {
  const { data, loading, error, refetch } = useGetCollectionQuery({
    variables: {
      id: collectionId,
      retailerId,
    },
    fetchPolicy: 'cache-and-network',
  })
  const [triggerGetParentCollectionQuery, { data: parentCollectionData }] =
    useGetParentCollectionLazyQuery()

  const collectionParentId = data?.collection?.parentId

  useEffect(() => {
    // The collections domain returns parentId = '0' if it has no parent
    if (collectionParentId && collectionParentId !== '0') {
      triggerGetParentCollectionQuery({
        variables: {
          id: collectionParentId,
        },
      })
    }
  }, [collectionParentId, triggerGetParentCollectionQuery])

  const messages = useDomainMessages({
    noStartDate: 'collectionsDomain.list.activeDates.noStart',
    noEndDate: 'collectionsDomain.list.activeDates.noEnd',
    displayed: 'collectionsDomain.view.storefrontBanner.display.displayed',
    activeAdminOnly: 'collectionsDomain.collectionsForm.section.details.status.activeAdminOnly',
    hidden: 'collectionsDomain.view.storefrontBanner.display.hidden',
    applyToAllWarehouses:
      'collectionsDomain.collectionsForm.section.details.applicableWarehouses.applyToAll',
    active: 'collectionsDomain.list.status.active',
    inactive: 'collectionsDomain.list.status.inactive',
    status: data?.collection ? collectionStatusMessage(data.collection) : null,
  })

  const headerDisplayStyles = useDomainMessages({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    graphics_browse_header:
      'collectionsDomain.collectionsForm.section.header.displayStyle.graphics',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    hero_browse_header: 'collectionsDomain.collectionsForm.section.header.displayStyle.hero',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    browse_header: 'collectionsDomain.collectionsForm.section.header.displayStyle.browse',
  })

  const [isModalOpen, showModal, hideModal] = useBooleanState(false)

  const goToEdit = useDomainGoToPath(routingConfig.edit, { collectionId })
  const goToList = useDomainGoToPath(routingConfig.list)

  if (loading) {
    return <LoadingPage />
  }

  if (error) {
    return <ErrorPage action={() => refetch()} />
  }

  const {
    name,
    startDate,
    endDate,
    parentId,
    header,
    imagePath,
    children,
    subjects,
    canEdit,
    storefrontBanner,
    requiredRetailerIds,
    slug,
  } = data.collection

  return (
    <PageWrapper>
      <CollectionsViewHeader
        retailerId={retailerId}
        modifyActionsAllowed={
          canEdit && canCurrentRetailerModifyCollection(data.collection, retailerId)
        }
        goToEdit={goToEdit}
        openDelete={showModal}
        name={name}
        routingConfig={routingConfig}
        baseBreadcrumbs={baseBreadcrumbs}
      />
      <PageContent>
        {/* Modal */}
        {isModalOpen && (
          <CollectionsDeleteCollectionModal
            collectionId={collectionId}
            onSuccess={goToList}
            onClose={hideModal}
            name={name}
          />
        )}

        {/* Detail Section */}
        <CollectionsViewSection titleId={'collectionsDomain.view.header.detail'}>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.detail.name">
              {name}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.detail.slug">
              {slug}
            </CollectionsViewInfo>
          </CollectionsViewRow>

          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.collectionsForm.section.details.status.label">
              {messages.status}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.detail.activeDates">
              {startDate ? (
                <FormattedDate timeZone="utc" value={startDate} />
              ) : (
                messages.noStartDate
              )}
              {' - '}
              {endDate ? <FormattedDate timeZone="utc" value={endDate} /> : messages.noEndDate}
            </CollectionsViewInfo>
          </CollectionsViewRow>

          {parentId && parentId !== '0' && (
            <CollectionsViewRow>
              <CollectionsViewInfo labelId="collectionsDomain.view.detail.parentCollection">
                {parentCollectionData?.collection?.name}
              </CollectionsViewInfo>
            </CollectionsViewRow>
          )}

          {!retailerId && (
            <CollectionsViewRow>
              <CollectionsViewInfo labelId="collectionsDomain.view.detail.applicableWarehouses">
                {requiredRetailerIds.length > 0
                  ? requiredRetailerIds.join(', ') // TODO: get warehouse names
                  : messages.applyToAllWarehouses}
              </CollectionsViewInfo>
            </CollectionsViewRow>
          )}
        </CollectionsViewSection>

        {/* Content Section */}
        <CollectionsViewSection titleId={'collectionsDomain.view.header.content'}>
          {subjects.length > 0 && (
            <CollectionsViewInfo
              width={500}
              labelId="collectionsDomain.collectionsForm.section.rules"
            >
              {subjects.map(subject => (
                <CollectionsViewRule key={subject.id} subject={subject} />
              ))}
            </CollectionsViewInfo>
          )}

          {children.length > 0 && <CollectionsViewChildCollections childCollections={children} />}
        </CollectionsViewSection>

        {/* Tile Section */}
        <CollectionsViewSection titleId={'collectionsDomain.view.header.tiles'}>
          <CollectionsViewImage imageUrl={imagePath} width="80px" height="80px" />
        </CollectionsViewSection>

        {/* Header Section */}
        <CollectionsViewSection titleId={'collectionsDomain.view.header.header'}>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.title">
              {header?.title}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.displayStyle">
              {header?.displayStyle && headerDisplayStyles[header.displayStyle]}
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.description">
              {header?.description}
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.color">
              <CollectionsColorInfo color={header?.color} />
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.backgroundColor">
              <CollectionsColorInfo color={header?.backgroundColor} />
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.webHeader">
              <CollectionsViewImage imageUrl={header?.imageUrl} width="600px" height="60px" />
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.header.mobileHeader">
              <CollectionsViewImage imageUrl={header?.mobileImageUrl} width="250px" height="80px" />
            </CollectionsViewInfo>
          </CollectionsViewRow>
        </CollectionsViewSection>

        {/* Storefront Banner Section */}
        <CollectionsViewSection titleId={'collectionsDomain.view.header.storefrontBanner'}>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.display">
              {storefrontBanner?.active ? messages.displayed : messages.hidden}
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.heading">
              {storefrontBanner?.heading}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.headingColor">
              <CollectionsColorInfo color={storefrontBanner?.headingColor} />
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.subHeading">
              {storefrontBanner?.subHeading}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.subHeadingColor">
              <CollectionsColorInfo color={storefrontBanner?.subHeadingColor} />
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.tag">
              {storefrontBanner?.tag}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.tagColor">
              <CollectionsColorInfo color={storefrontBanner?.tagColor} />
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.cta">
              {storefrontBanner?.cta}
            </CollectionsViewInfo>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.backgroundColor">
              <CollectionsColorInfo color={storefrontBanner?.backgroundColor} />
            </CollectionsViewInfo>
          </CollectionsViewRow>
          <CollectionsViewRow>
            <CollectionsViewInfo labelId="collectionsDomain.view.storefrontBanner.banner">
              <CollectionsViewImage
                imageUrl={storefrontBanner?.image}
                width="250px"
                height="80px"
              />
            </CollectionsViewInfo>
          </CollectionsViewRow>
        </CollectionsViewSection>
      </PageContent>
    </PageWrapper>
  )
}
